import { api, BASE_URL } from '../api';
import { useAuthStore } from '../auth-store';

/**
 * The resume-position write.
 *
 * Deliberately NOT a react-query mutation: the shared MutationCache toasts
 * every mutation failure, and this fires every ten seconds during playback —
 * one flaky moment on mobile data would bury the learner in error toasts about
 * a write they never asked for. Failures are swallowed; the next beat carries
 * the position anyway.
 */
export async function writeLessonPosition(
  courseId: string,
  lessonId: string,
  positionSeconds: number,
  extra: { maxPositionSeconds?: number; ended?: boolean } = {},
): Promise<{ ok: boolean; status?: number }> {
  try {
    await api.put(`/courses/${courseId}/lessons/${lessonId}/position`, {
      positionSeconds,
      ...extra,
    });
    return { ok: true };
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return { ok: false, status };
  }
}

/**
 * Last write as the page goes away.
 *
 * `sendBeacon` cannot carry an Authorization header and our access token lives
 * in memory only (never a cookie, per CLAUDE.md), so this is `fetch` with
 * `keepalive`. Best-effort by design: it bypasses the axios refresh
 * interceptor, so an access token that expired mid-lesson loses this one write
 * — bounded to the ten-second tick. `credentials` is omitted on purpose (the
 * token is a header; including cookies only tightens the CORS rules).
 *
 * It does not fire at all in the Capacitor Android app, where backgrounding
 * never unloads the document — the blur/pause beats are the net there.
 */
export function flushLessonPositionKeepalive(
  courseId: string,
  lessonId: string,
  positionSeconds: number,
  extra: { maxPositionSeconds?: number; ended?: boolean } = {},
): void {
  const token = useAuthStore.getState().token;
  if (!token) return;
  try {
    void fetch(`${BASE_URL}/courses/${courseId}/lessons/${lessonId}/position`, {
      method: 'PUT',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ positionSeconds, ...extra }),
    }).catch(() => {});
  } catch {
    /* the document is already tearing down */
  }
}
